import { useState } from 'react'
import { ProTable } from '@ant-design/pro-components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { Alert, Button, Empty, Tag } from 'antd'
import { TableRowActions } from '@/components/table-row-actions.tsx'
import { CREATED_AT_SORT_DIRECTIONS, DEFAULT_PAGE_SEARCH } from '@/const/list.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { useMemberCreatorOptions } from '@/features/access/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import { usePageTableNavigation } from '@/hooks/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getCreatedAtSearch, getDateRangeFormValue, type DateRangeFormValue } from '@/utils/list-query.ts'
import { formatAmount } from '@/utils/money.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'
import { BillDetail } from './bill-detail.tsx'
import { BillingAutomationRuns } from './billing-automation-runs.tsx'
import {
  GROUP_SETTLEMENT_COLORS,
  GROUP_SETTLEMENT_LABELS,
  GROUP_STATUS_COLORS,
  GROUP_STATUS_LABELS
} from './const/group-status.ts'
import type { FinanceListProps, FinanceListSearch } from './finance-search.ts'
import { GroupActions, type GroupAction } from './group-actions.tsx'
import { GroupBatchDialog, type GroupBatchTarget } from './group-batch-dialog.tsx'
import { GroupDetail } from './group-detail.tsx'
import { GroupExpandedBills } from './group-expanded-bills.tsx'
import { GroupMoneyEntry } from './group-money-entry.tsx'
import { useBillFilterOptions } from './hooks/use-bill-filter-options.ts'
import { MonthlyGroupSupplement } from './monthly-group-supplement.tsx'

type GroupFilters = Partial<FinanceListSearch> & { createdAtRange?: DateRangeFormValue }

export function GroupLedger({
  canManage,
  search,
  setSearch,
  customerScoped = false,
  hideCreatedTimeFilter = false,
  defaultSearch = DEFAULT_PAGE_SEARCH
}: FinanceListProps & {
  customerScoped?: boolean
  hideCreatedTimeFilter?: boolean
  defaultSearch?: FinanceListSearch
}) {
  const router = useRouter()
  const [runsOpen, setRunsOpen] = useState(false),
    [supplementOpen, setSupplementOpen] = useState(false)
  const [selected, setSelected] = useState<number[]>([])
  const [batch, setBatch] = useState<{ target: GroupBatchTarget; action: Exclude<GroupAction, 'receipt'> }>()
  const [initialAction, setInitialAction] = useState<'void' | 'edit' | 'issue' | 'collection'>()
  const [receiptGroupId, setReceiptGroupId] = useState<number>()
  const trpc = useTRPC()
  const query = useQuery({ ...trpc.bill.groupList.queryOptions(search), placeholderData: keepPreviousData }),
    [id, setId] = useState<number>()
  const [billId, setBillId] = useState<number>()
  const filterOptions = useBillFilterOptions(search, 'bill.group.read')
  const creators = useMemberCreatorOptions('bill.group.read', search.creatorMemberId)
  const table = usePageTableNavigation<FinanceListSearch, GroupFilters>({
    search,
    defaultSearch,
    navigate: ({ search: next }) => setSearch(typeof next === 'function' ? next(search) : next),
    getFormInitialValues: (value) => ({
      keyword: value.keyword ?? (value.id ? String(value.id) : undefined),
      organizationId: value.organizationId,
      customerId: value.customerId,
      status: value.status,
      collectionHeld: value.collectionHeld,
      creatorMemberId: value.creatorMemberId,
      createdAtRange: getDateRangeFormValue(value.createdFrom, value.createdTo)
    }),
    getSearchFromForm: (value) => ({
      id: undefined,
      keyword: value.keyword?.trim() || undefined,
      organizationId: value.organizationId,
      customerId: value.customerId,
      status: value.status,
      collectionHeld: value.collectionHeld,
      creatorMemberId: value.creatorMemberId,
      ...getCreatedAtSearch(value.createdAtRange)
    }),
    showTotal: (total) => `共 ${total} 条`
  })
  return (
    <>
      {query.isError && <Alert type="error" title={query.error.message} />}
      <ProTable
        search={{
          labelWidth: 'auto',
          defaultCollapsed: false,
          collapseRender: (collapsed) => <span className="whitespace-nowrap">{collapsed ? '展开' : '收起'}</span>
        }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无符合条件的收款账单组" /> }}
        rowKey="id"
        rowSelection={{
          selectedRowKeys: selected,
          onChange: (keys) => setSelected(keys.map(Number)),
          preserveSelectedRowKeys: false
        }}
        toolBarRender={() => [
          ...(!customerScoped
            ? [
                <Button key="runs" onClick={() => setRunsOpen(true)}>
                  自动任务记录
                </Button>
              ]
            : []),
          ...(canManage && !customerScoped
            ? [
                <Button key="supplement" onClick={() => setSupplementOpen(true)}>
                  按客户月份补组
                </Button>
              ]
            : []),

          <GroupActions
            key="selected-batch"
            receiptHref={
              router.buildLocation({
                to: '/finance/groups/receipt-batch',
                search: { target: { mode: 'ids', ids: selected } }
              }).href
            }
            canManage={canManage}
            label="批量操作"
            disabled={selected.length === 0 || query.isFetching}
            onAction={(action) => {
              if (action !== 'receipt') setBatch({ action, target: { mode: 'ids', ids: selected } })
            }}
          />,
          <GroupActions
            key="search-batch"
            receiptHref={
              router.buildLocation({
                to: '/finance/groups/receipt-batch',
                search: { target: { mode: 'search', search } }
              }).href
            }
            canManage={canManage}
            label="按搜索批量操作"
            disabled={!query.data?.total || query.isFetching}
            onAction={(action) => {
              if (action !== 'receipt') setBatch({ action, target: { mode: 'search', search } })
            }}
          />
        ]}
        formRef={table.formRef}
        form={{ initialValues: table.formInitialValues }}
        columnsState={{
          persistenceKey: customerScoped ? 'customer-documents-groups:table:v2' : 'finance:groups:v2',
          persistenceType: 'localStorage',
          defaultValue: { billingMonth: { show: true } }
        }}
        dataSource={query.data?.data ?? []}
        loading={query.isFetching}
        scroll={{ x: 'max-content' }}
        expandable={{
          rowExpandable: (row) => row.billCount > 0,

          /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
          expandedRowRender: (row, _index, _indent, expanded) =>
            expanded ? <GroupExpandedBills groupId={row.id} onBill={setBillId} /> : null
          /* eslint-enable max-params */
        }}
        columns={[
          {
            title: '催收状态',
            dataIndex: 'collectionHeld',
            hideInTable: true,
            valueType: 'select',
            fieldProps: {
              options: [
                { label: '正常催收', value: false },
                { label: '暂缓催收', value: true }
              ]
            }
          },

          {
            title: 'ID',
            dataIndex: 'id',
            copyable: true,
            valueType: 'digit',
            search: false,
            width: '5.5rem',
            fieldProps: { min: 1, precision: 0, placeholder: '输入 ID' }
          },
          {
            title: '关键词 / ID',
            dataIndex: 'keyword',
            hideInTable: true,
            order: 100,
            fieldProps: { placeholder: '租单号、组单号或账单组 ID', allowClear: true }
          },
          {
            title: '经营主体',
            dataIndex: 'organizationId',
            hideInTable: true,
            valueType: 'select',
            fieldProps: { ...filterOptions.organization, placeholder: '搜索经营主体', allowClear: true }
          },
          {
            title: '客户',
            dataIndex: 'customerId',
            search: customerScoped ? false : undefined,
            hideInTable: true,
            valueType: 'select',
            fieldProps: { ...filterOptions.customer, placeholder: '搜索客户', allowClear: true }
          },
          { title: '组单号', dataIndex: 'groupNo', width: '14rem', search: false },
          { title: '客户', dataIndex: 'customerName', width: '12rem', ellipsis: true, search: false },
          { title: '主体', dataIndex: 'organizationName', width: '14rem', ellipsis: true, search: false },
          {
            title: '状态',
            dataIndex: 'status',
            width: '6rem',
            valueEnum: GROUP_STATUS_LABELS,
            render: (_, row) => <Tag color={GROUP_STATUS_COLORS[row.status]}>{GROUP_STATUS_LABELS[row.status]}</Tag>
          },
          {
            title: '组结算状态',
            dataIndex: 'settlementStatus',
            search: false,
            width: '8rem',
            render: (_, row) => (
              <Tag color={row.status === 'voided' ? 'default' : GROUP_SETTLEMENT_COLORS[row.settlementStatus]}>
                {row.status === 'voided' ? '已作废' : GROUP_SETTLEMENT_LABELS[row.settlementStatus]}
              </Tag>
            )
          },
          {
            title: '账单数',
            dataIndex: 'billCount',
            width: '6rem',
            align: 'right',
            search: false,
            render: (_, row) => `${row.billCount} 笔`
          },
          {
            title: '出单 / 预计金额',
            dataIndex: 'totalAmountSnapshot',
            render: (_, row) =>
              `¥${formatAmount(row.status === 'draft' ? row.outstandingAmount : row.totalAmountSnapshot)}`,
            width: '10rem',
            align: 'right',
            valueType: 'money',
            search: false
          },
          {
            title: '当前待收',
            dataIndex: 'outstandingAmount',
            align: 'right',
            valueType: 'money',
            search: false,
            width: '10rem'
          },
          {
            title: '归属月份',
            dataIndex: 'billingMonth',
            search: false,
            width: '7rem',
            renderText: (value) => value || '未指定'
          },
          {
            title: '催收状态',
            key: 'collectionState',
            search: false,
            width: '7rem',
            render: (_, row) => (
              <Tag color={row.collectionHeldAt ? 'warning' : 'default'}>
                {row.collectionHeldAt ? '暂缓催收' : '正常催收'}
              </Tag>
            )
          },
          {
            title: '来源',
            key: 'source',
            search: false,
            width: '6rem',
            render: (_, row) => (row.source === 'automatic' ? '系统生成' : '人工创建')
          },
          {
            title: '创建时间',
            dataIndex: 'createdAtRange',
            search: hideCreatedTimeFilter ? false : undefined,
            hideInTable: true,
            fieldProps: { presets: RANGE_PRESETS },
            valueType: 'dateRange'
          },
          {
            title: '创建人',
            dataIndex: 'creatorMemberId',
            hideInTable: true,
            valueType: 'select',
            fieldProps: {
              allowClear: true,
              options: creators.options,
              loading: creators.isLoading,
              ...proRemoteSearch(creators.onSearch)
            }
          },
          {
            title: '创建人',
            key: 'creator',
            width: '10rem',
            search: false,
            render: (_, row) => (
              <UserAvatarName
                avatar={row.creator?.avatar}
                name={row.creator?.name ?? '机器人'}
                isSystem={row.source === 'automatic' || row.creator?.isSystem}
                size={28}
              />
            )
          },
          {
            title: '创建时间',
            dataIndex: 'createdAt',
            width: '11rem',
            valueType: 'dateTime',
            search: false,
            sorter: true,
            sortDirections: CREATED_AT_SORT_DIRECTIONS,
            sortOrder: search.sortOrder === 'asc' ? 'ascend' : 'descend'
          },
          {
            align: 'center',
            title: '操作',
            valueType: 'option',
            fixed: 'right',
            width: '10rem',
            render: (_, row) => (
              <TableRowActions
                resource="bill.group"
                maxVisibleActions={1}
                actions={[
                  {
                    key: 'view',
                    label: '查看',
                    onClick: () => {
                      setInitialAction(undefined)
                      setId(row.id)
                    }
                  },
                  ...(canManage && row.status !== 'voided'
                    ? [
                        {
                          key: 'collection',
                          label: '归属月份 / 催收设置',
                          onClick: () => {
                            setInitialAction('collection')
                            setId(row.id)
                          }
                        }
                      ]
                    : []),
                  ...(canManage && row.status === 'draft'
                    ? [
                        {
                          key: 'edit',
                          label: '编辑草稿',
                          onClick: () => {
                            setInitialAction('edit')
                            setId(row.id)
                          }
                        },
                        {
                          key: 'issue',
                          label: '确认出单',
                          onClick: () => {
                            setInitialAction('issue')
                            setId(row.id)
                          }
                        }
                      ]
                    : []),
                  ...(row.status !== 'draft'
                    ? [
                        {
                          key: 'print',
                          label: (
                            <a
                              href={router.buildLocation({ to: '/finance/receipts', search: { ids: [row.id] } }).href}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              打印收据
                            </a>
                          )
                        }
                      ]
                    : []),
                  ...(canManage && row.status === 'issued'
                    ? [
                        {
                          key: 'receipt',
                          label: '登记收款',
                          disabled: row.settlementStatus === 'settled',
                          onClick: () => setReceiptGroupId(row.id)
                        }
                      ]
                    : []),
                  ...(canManage && row.status !== 'voided'
                    ? [
                        {
                          key: 'void',
                          label: '作废并释放账单',
                          danger: true,
                          onClick: () => {
                            setInitialAction('void')
                            setId(row.id)
                          }
                        }
                      ]
                    : [])
                ]}
              />
            )
          }
        ]}
        onChange={
          /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
          (_, __, sorter, extra) => {
            setSelected([])
            if (extra.action !== 'sort') {
              return
            }
            const sort = Array.isArray(sorter) ? sorter[0] : sorter
            setSearch({
              ...search,
              current: 1,
              sortField: 'createdAt',
              sortOrder: sort.order === 'ascend' ? 'asc' : 'desc'
            })
          }
          /* eslint-enable max-params */
        }
        onSubmit={(values) => {
          setSelected([])
          table.onSubmit(values)
        }}
        onReset={() => {
          setSelected([])
          table.onReset()
        }}
        pagination={{ ...table.pagination, total: query.data?.total }}
        options={{ reload: () => void query.refetch() }}
      />
      {runsOpen && <BillingAutomationRuns canManage={canManage} onClose={() => setRunsOpen(false)} />}
      {supplementOpen && <MonthlyGroupSupplement onClose={() => setSupplementOpen(false)} />}
      {batch && (
        <GroupBatchDialog
          target={batch.target}
          action={batch.action}
          onClose={() => {
            setBatch(undefined)
            setSelected([])
          }}
        />
      )}
      {billId && <BillDetail key={billId} billId={billId} canManage={canManage} onClose={() => setBillId(undefined)} />}
      {receiptGroupId && (
        <GroupMoneyEntry key={receiptGroupId} groupId={receiptGroupId} onClose={() => setReceiptGroupId(undefined)} />
      )}
      {id && (
        <GroupDetail
          initialAction={initialAction}
          key={`${id}:${initialAction ?? 'detail'}`}
          id={id}
          canManage={canManage}
          onClose={() => setId(undefined)}
        />
      )}
    </>
  )
}
